// 核心依赖 - 首屏必需
import { initializeMonitoring, aegis } from "@/utils/monitoring";
import { installActivity618DebugConsole } from "@/utils/activity618Debug";
import { initTokenManager } from "@/utils/tokenManager";
import { onAuthReset } from "@/utils/authResetChannel";
import { useUserStore } from "@/store/user";
import { clearBoardLocalData } from "@/utils/boardLocalDataCleanup";
import { createApp } from "vue";
import App from "./App.vue";
import router from "@/router";
import { createPinia } from "pinia";
import piniaPluginPersistedstate from "pinia-plugin-persistedstate";
import { createHead } from "@unhead/vue";
import { VueQueryPlugin } from "@tanstack/vue-query";

// 关键样式 - 首屏必需
import "@/assets/styles/reset.less";
import "@/assets/styles/antd.less";
import "@/assets/styles/public.less";
import "@/styles/cls-optimization.less";
import "virtual:svg-icons-register";
import '@xmiles/holopix-designer/tokens.css'
import "@xmiles/holopix-components/style.css"

// 核心组件和指令
import SvgIcon from "@/components/SvgIcon/index.vue";
import LazyImage from "vue-lazyload";
import directives from "./directives";
import PluginsPlugin from "./plugins";

// 延迟加载的依赖将在应用启动后异步加载
let sensors: any = null;

// 启动 Aegis 监控 (异步初始化)
initializeMonitoring();

// 启动 Token 管理器（跨标签页同步 + 过期日志）
initTokenManager();

const app = createApp(App);

/**
 * 初始化传感器 (Sensors)
 * 保持原有逻辑：在 Vue 初始化流程中加载
 */
async function initializeSensors() {
  try {
    const sensorsModule = await import("@/assets/sensors");
    sensors = sensorsModule.sensors;

    // 挂载到 Vue 原型
    if (sensors) {
      app.config.globalProperties.$sensors = sensors;
    }

    // 异步增强传感器追踪
    sensorsModule.enhanceSensorsTracking().catch((error) => {
      console.warn("传感器增强初始化失败:", error);
    });
  } catch (error) {
    console.warn("传感器初始化失败:", error);
  }
}

// 启动传感器初始化
initializeSensors();

app.config.errorHandler = function (err: unknown, _vm: unknown, info: string) {
  console.warn(`Error: ${(err as Error).toString()}\nStack: ${(err as Error).stack}\nInfo: ${info}`, err, info);
  aegis?.error(`Error: ${(err as Error).toString()}\nStack: ${(err as Error).stack}\nInfo: ${info}`);
};

const pinia = createPinia();
pinia.use(piniaPluginPersistedstate);

// 开发环境：暴露 Pinia store 到全局，便于调试
if (import.meta.env.DEV) {
  import("pinia").then(({ storeToRefs }) => {
    window.__PINIA_DEBUG__ = {
      getStore: (storeName: string) => {
        // 动态导入 store，避免循环依赖
        if (storeName === "user") {
          return import("@/store/user").then((m) => m.useUserStore());
        }
        return Promise.resolve(null);
      }
    };
    console.log("[DEV] Pinia 调试接口已启用，使用: await window.__PINIA_DEBUG__.getStore('user')");
  });
}

const head = createHead();
app.use(head);

// 两大基础插件优先注册
app.use(router).use(pinia);

// 注册跨标签页 auth-reset 监听：当其他标签页登出/token清除时，同步清理当前页 Pinia store
onAuthReset(() => {
  useUserStore().resetSharedAuthUser();
  void clearBoardLocalData();
});

// 注册指令
app.use(directives);

// 自定义插件集合,次级插件在自定义插件之后
app.use(PluginsPlugin).use(VueQueryPlugin);

// 自定义组件
app.use(LazyImage, { attempt: 1 });

installActivity618DebugConsole();

// 开始挂载
app.mount("#app");

app.component("SvgIcon", SvgIcon);

// 延迟加载非关键资源
Promise.all([import("@/lazyLoadAssets")]).then(([{ lazyLoadAssets }]) => {
  lazyLoadAssets();
});

// PWA/SW 已下线（HP-9574）：历史版本注册的 sw.js 必须主动注销，否则旧 SW 会继续拦截请求、
// 用 pino-cache-* 缓存返回旧静态资源，并与版本检测逻辑互相干扰；其缓存一并清理。
// 注销后已打开的页面仍受旧 SW 控制一次加载，刷新/下次进入即完全脱离
if (import.meta.env.PROD && "serviceWorker" in navigator) {
  navigator.serviceWorker
    .getRegistrations()
    .then((registrations) => registrations.forEach((registration) => registration.unregister()))
    .catch(() => {
      // 注销失败不应阻断页面运行，静默忽略
    });
  if ("caches" in window) {
    caches
      .keys()
      .then((keys) =>
        Promise.all(
          keys
            // 只清理本应用创建的缓存（pino-cache-<版本> / pino-version-meta），避免误删其他库的缓存
            .filter((key) => key.startsWith("pino-"))
            .map((key) => caches.delete(key))
        )
      )
      .catch(() => {});
  }
}
